import { EventDTO } from '../../../../dto/event/event.dto';

export type EventCategoryColorsMap = Record<string, string>;

export function normalizeEventCategoryColors(
  value: unknown
): EventCategoryColorsMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const result: EventCategoryColorsMap = {};
  for (const [key, color] of Object.entries(value as Record<string, unknown>)) {
    const name = (key || '').trim();
    if (!name) {
      continue;
    }
    const colorStr =
      typeof color === 'string' && color.trim() !== '' ? color.trim() : '#1976d2';
    result[name] = colorStr;
  }
  return result;
}

function categoryNameFromExtra(event: EventDTO | null | undefined): string | null {
  const category = event?.event_extra?.category;
  if (typeof category !== 'string') {
    return null;
  }
  const trimmed = category.trim();
  return trimmed !== '' ? trimmed : null;
}

/** Category names from a batch of events (serie/gruppe: sibling may lack event_extra). */
export interface EventCategoryInheritanceContext {
  categoryByGuid: Map<string, string>;
  categoryByLinkedGroupId: Map<string, string>;
  categoryByEventId: Map<number, string>;
}

export function buildEventCategoryInheritanceContext(
  events: EventDTO[]
): EventCategoryInheritanceContext {
  const categoryByGuid = new Map<string, string>();
  const categoryByLinkedGroupId = new Map<string, string>();
  const categoryByEventId = new Map<number, string>();

  for (const event of events) {
    const name = categoryNameFromExtra(event);
    if (!name) {
      continue;
    }
    const guid = typeof event.guid === 'string' ? event.guid.trim() : '';
    if (guid) {
      categoryByGuid.set(guid, name);
    }
    if (typeof event.event_id === 'number' && event.event_id > 0) {
      categoryByEventId.set(event.event_id, name);
    }
    const groupId =
      typeof event.linked_event_group_id === 'string'
        ? event.linked_event_group_id.trim()
        : '';
    if (groupId) {
      categoryByLinkedGroupId.set(groupId, name);
    }
  }

  return { categoryByGuid, categoryByLinkedGroupId, categoryByEventId };
}

export function resolveEventCategoryName(
  event: EventDTO | null | undefined,
  context?: EventCategoryInheritanceContext | null
): string | null {
  const own = categoryNameFromExtra(event);
  if (own) {
    return own;
  }
  if (!event || !context) {
    return null;
  }

  const parentGuid =
    typeof event.parent_event_guid === 'string'
      ? event.parent_event_guid.trim()
      : '';
  if (parentGuid && context.categoryByGuid.has(parentGuid)) {
    return context.categoryByGuid.get(parentGuid) ?? null;
  }

  if (
    typeof event.parent_event_id === 'number' &&
    event.parent_event_id > 0 &&
    context.categoryByEventId.has(event.parent_event_id)
  ) {
    return context.categoryByEventId.get(event.parent_event_id) ?? null;
  }

  const groupId =
    typeof event.linked_event_group_id === 'string'
      ? event.linked_event_group_id.trim()
      : '';
  if (groupId && context.categoryByLinkedGroupId.has(groupId)) {
    return context.categoryByLinkedGroupId.get(groupId) ?? null;
  }

  return null;
}

function lookupCategoryColor(
  categoryName: string,
  categoryColors: EventCategoryColorsMap
): string | null {
  const direct = categoryColors[categoryName];
  if (direct) {
    return direct;
  }
  const lower = categoryName.toLowerCase();
  for (const [key, color] of Object.entries(categoryColors)) {
    if (key.toLowerCase() === lower) {
      return color;
    }
  }
  return null;
}

export function resolveEventCategoryBackgroundColor(
  event: EventDTO | null | undefined,
  categoryColors: EventCategoryColorsMap | null | undefined,
  context?: EventCategoryInheritanceContext | null
): string | null {
  const name = resolveEventCategoryName(event, context);
  if (!name || !categoryColors) {
    return null;
  }
  return lookupCategoryColor(name, categoryColors);
}

export function resolveEventCategoryBorderColor(background: string): string {
  return background;
}
